import _ from 'underscore';

import {removeGaps} from 'biojs/Util';
import antibodyAnalysisStore from 'molbio/antibody-engineering/antibodyAnalysis';
import {CDRAnnotation, CDRDefinition} from 'molbio/antibody-engineering/AntibodyEngineeringConstants';
import {RegionSelectable} from 'molbio/maps/selectables/Selectables';
import Protein from 'molbio/proteins/Protein';

import {getRealToGapMap} from './alignments/getRealToGapMap';
import {ProteinAnnotationsViewOptions} from './ProteinAnnotationsView';
import ProteinCDRView, {formatRegionName} from './ProteinCDRAnnotationsView';

interface ConstructorOptions extends ProteinAnnotationsViewOptions {
  aminoAcids: string;
}

export default class ProteinAlignmentCDRAnnotationsView extends ProteinCDRView {
  realToGapMap: Array<number>;

  constructor(options: ConstructorOptions) {
    super(options);
    this.className = 'antibodyAlignmentCDRAnnotations';

    this.realToGapMap = getRealToGapMap(options.aminoAcids);
  }

  _getAminoAcids(): string {
    return this.cachedAminoAcids;
  }

  getPolymerLength(): number {
    return this.cachedAminoAcids.length;
  }

  /**
   * We don't need to add the listener for amino acids because alignments are read-only,
   * so they'll never change
   */
  _registerAnnotationListeners(): void {
    this._addVisibilityOptionsListener();
  }

  _updateAfterProteinChange(): void {
    this.update();
  }

  clickAction(e: Event, d: RegionSelectable<Protein>): void {
    this.tabstate.get('selection').setEndpoints(d.start(), d.end());
  }

  dblClickAction(): void {}

  _convertToGappedAnnotations(annotations: Array<CDRAnnotation>): Array<CDRAnnotation> {
    return annotations.map((annotation) => ({
      ...annotation,
      start: this.realToGapMap[annotation.start],
      end: this.realToGapMap[annotation.end] || this.realToGapMap[this.realToGapMap.length - 1] + 1,
    }));
  }

  async _fetchCDRAnnotations(): Promise<void> {
    if (!this.option.get('visible')) {
      return;
    }
    const unGappedAminoAcids = removeGaps(this._getAminoAcids());
    const [chains] = await antibodyAnalysisStore.getCDRAnnotations(
      [unGappedAminoAcids],
      (this.option.getSetting('CDRDefinition')!.get('value') as string).toLowerCase() as CDRDefinition
    );
    const formattedRegions = chains?.map((chain) =>
      chain.regions.map((reg) => ({...reg, name: formatRegionName(reg.name, chain.chainType)}))
    );
    // eslint-disable-next-line no-restricted-syntax
    const flattenedRegions = formattedRegions ? _.flatten(formattedRegions) : [];
    this.cachedAnnotations = this._convertToGappedAnnotations(flattenedRegions);
    this._updateAfterProteinChange();
  }
}
